import { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import type { QuizQuestion, QuizOption } from "@/types/quiz"

interface QuestionFormProps {
  initialData?: QuizQuestion
  onSubmit: (data: Partial<QuizQuestion>) => void
  onCancel: () => void
}

export function QuestionForm({ initialData, onSubmit, onCancel }: QuestionFormProps) {
  const [options, setOptions] = useState<QuizOption[]>(
    initialData?.options || [{ value: '', label: '', description: '', traits: [] }]
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    const questionData: Partial<QuizQuestion> = {
      question: formData.get('question') as string,
      description: formData.get('description') as string,
      category: formData.get('category') as string,
      active: true,
      options: options.map((option, index) => ({
        ...option,
        value: formData.get(`option-value-${index}`) as string,
        label: formData.get(`option-label-${index}`) as string,
        description: formData.get(`option-description-${index}`) as string,
        traits: (formData.get(`option-traits-${index}`) as string).split(',').map(t => t.trim())
      }))
    }

    onSubmit(questionData)
  }

  const addOption = () => {
    setOptions([...options, { value: '', label: '', description: '', traits: [] }])
  }

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="question">Question</Label>
          <Input
            id="question"
            name="question"
            defaultValue={initialData?.question}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            name="description"
            defaultValue={initialData?.description}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Input
            id="category"
            name="category"
            defaultValue={initialData?.category}
            required
          />
        </div>

        <div className="space-y-4">
          <Label>Options</Label>
          {options.map((option, index) => (
            <div key={index} className="space-y-2 p-4 border rounded-md">
              <div className="flex justify-between">
                <h4 className="font-medium">Option {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeOption(index)}
                >
                  Remove
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`option-value-${index}`}>Value</Label>
                <Input
                  id={`option-value-${index}`}
                  name={`option-value-${index}`}
                  defaultValue={option.value}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`option-label-${index}`}>Label</Label>
                <Input
                  id={`option-label-${index}`}
                  name={`option-label-${index}`}
                  defaultValue={option.label}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`option-description-${index}`}>Description</Label>
                <Textarea
                  id={`option-description-${index}`}
                  name={`option-description-${index}`}
                  defaultValue={option.description}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`option-traits-${index}`}>
                  Traits (comma-separated)
                </Label>
                <Input
                  id={`option-traits-${index}`}
                  name={`option-traits-${index}`}
                  defaultValue={option.traits.join(', ')}
                  required
                />
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addOption}>
            Add Option
          </Button>
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit">Save Question</Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}